import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { User, Target, Scale, Ruler, Activity, Save, Loader, Calendar, Edit3, Check, Mail, Gift, Copy, Send, CheckCircle, Users, Crown } from 'lucide-react';

const ProfilePage = ({ onAuthClick }) => {
  const { user, profile, updateProfile, isAuthenticated } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    goal: '',
    weight: '',
    target_weight: '',
    height: '',
    age: '',
    activity_level: '',
    email_newsletter: true
  });

  // États pour changement email
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');

  // États pour parrainage
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  const [referralStats, setReferralStats] = useState({ invites: 0, converted: 0, rewards: 0 });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        goal: profile.goal || '',
        weight: profile.weight || '',
        target_weight: profile.target_weight || '',
        height: profile.height || '',
        age: profile.age || '',
        activity_level: profile.activity_level || '',
        email_newsletter: profile.email_newsletter !== false
      });
      loadReferralStats();
    }
  }, [profile]);

  const loadReferralStats = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('referrals')
      .select('*')
      .eq('referrer_id', user.id);
    
    if (data) {
      setReferralStats({
        invites: data.length,
        converted: data.filter(r => r.converted).length,
        rewards: data.filter(r => r.reward_applied).length
      });
    }
  };

  const goals = [
    { id: 'weight-loss', emoji: '🔥', label: 'Perte de poids' },
    { id: 'muscle-building', emoji: '💪', label: 'Prise de muscle' },
    { id: 'health', emoji: '🌱', label: 'Santé générale' },
    { id: 'energy', emoji: '⚡', label: 'Plus d\'énergie' },
    { id: 'maintenance', emoji: '⚖️', label: 'Maintien' }
  ];

  const activityLevels = [
    { id: 'sedentary', label: 'Sédentaire', desc: 'Peu ou pas d\'exercice' },
    { id: 'light', label: 'Léger', desc: '1-3 fois/semaine' },
    { id: 'moderate', label: 'Modéré', desc: '3-5 fois/semaine' },
    { id: 'active', label: 'Actif', desc: '6-7 fois/semaine' },
    { id: 'very-active', label: 'Très actif', desc: 'Athlète' }
  ];

  const handleSave = async () => {
    setSaving(true);
    await updateProfile(formData);
    setTimeout(() => {
      setSaving(false);
      setEditing(false);
    }, 300);
  };

  // Changement d'email
  const handleEmailChange = async () => {
    if (!newEmail || newEmail === user?.email) {
      setEmailMessage('Entrez une nouvelle adresse email');
      return;
    }
    setEmailLoading(true);
    setEmailMessage('');

    const { error } = await supabase.auth.updateUser({ email: newEmail });

    if (error) {
      setEmailMessage('Erreur : ' + error.message);
    } else {
      setEmailMessage('Email de confirmation envoyé ! Vérifiez vos deux boîtes mail.');
      setNewEmail('');
    }
    setEmailLoading(false);
  };

  // Envoi d'invitation
  const handleInvite = async () => {
    if (!inviteEmail) {
      setInviteMessage('Entrez l\'email de votre ami(e)');
      return;
    }
    if (inviteEmail === user?.email) {
      setInviteMessage('Vous ne pouvez pas vous inviter vous-même !');
      return;
    }
    setInviteLoading(true);
    setInviteMessage('');

    try {
      // Appeler l'Edge Function pour envoyer l'invitation
      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/send-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          inviteEmail,
          referrerId: user.id,
          referrerName: profile?.name || 'Un ami'
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setInviteMessage('Invitation envoyée ! 🎉');
        setInviteEmail('');
        loadReferralStats();
      } else {
        setInviteMessage(result.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setInviteMessage('Erreur de connexion');
    }
    setInviteLoading(false);
  };

  // Copier le lien de parrainage
  const copyReferralLink = () => {
    const link = `https://moncoach.eu/?ref=${user?.id}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isAuthenticated) {
    return (
      <div className="profile-page">
        <div className="profile-locked-card">
          <div className="locked-emoji">👤</div>
          <h2>Votre espace personnel</h2>
          <p>Inscrivez-vous pour sauvegarder votre profil et obtenir des conseils 100% personnalisés !</p>
          <button className="primary-btn large" onClick={onAuthClick}>
            Créer mon profil gratuitement
          </button>
          <div className="locked-benefits">
            <span>✅ Historique sauvegardé</span>
            <span>✅ Conseils sur mesure</span>
            <span>✅ Suivi des progrès</span>
          </div>
        </div>
      </div>
    );
  }

  const currentGoal = goals.find(g => g.id === formData.goal);
  const weightDiff = formData.weight && formData.target_weight 
    ? Math.abs(parseFloat(formData.weight) - parseFloat(formData.target_weight)).toFixed(1)
    : null;

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Header avec avatar */}
        <div className="profile-header-card">
          <div className="profile-avatar">
            {profile?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
          </div>
          <h1>{profile?.name || 'Mon Profil'}</h1>
          <p className="profile-email">{user?.email}</p>
          {currentGoal && (
            <span className="goal-badge">{currentGoal.emoji} {currentGoal.label}</span>
          )}
        </div>

        {/* Stats rapides */}
        <div className="profile-stats">
          <div className="profile-stat">
            <Scale size={20} />
            <div>
              <span className="stat-value">{formData.weight || '--'}</span>
              <span className="stat-unit">kg</span>
            </div>
            <span className="stat-label">Actuel</span>
          </div>
          <div className="profile-stat">
            <Target size={20} />
            <div>
              <span className="stat-value">{formData.target_weight || '--'}</span>
              <span className="stat-unit">kg</span>
            </div>
            <span className="stat-label">Objectif</span>
          </div>
          <div className="profile-stat">
            <Ruler size={20} />
            <div>
              <span className="stat-value">{formData.height || '--'}</span>
              <span className="stat-unit">cm</span>
            </div>
            <span className="stat-label">Taille</span>
          </div>
          <div className="profile-stat highlight">
            <Calendar size={20} />
            <div>
              <span className="stat-value">{weightDiff || '--'}</span>
              <span className="stat-unit">kg</span>
            </div>
            <span className="stat-label">À {parseFloat(formData.target_weight) < parseFloat(formData.weight) ? 'perdre' : 'prendre'}</span>
          </div>
        </div>

        {/* SECTION PARRAINAGE */}
        <div className="referral-card">
          <div className="referral-header">
            <Gift size={24} />
            <div>
              <h2>Invitez vos amis, gagnez 1 mois offert !</h2>
              <p>Pour chaque ami qui s'inscrit et essaie MonCoach, vous recevez 1 mois d'abonnement gratuit.</p>
            </div>
          </div>

          <div className="referral-stats">
            <div className="referral-stat">
              <Users size={20} />
              <span className="stat-number">{referralStats.invites}</span>
              <span className="stat-text">Invitations envoyées</span>
            </div>
            <div className="referral-stat">
              <CheckCircle size={20} />
              <span className="stat-number">{referralStats.converted}</span>
              <span className="stat-text">Amis inscrits</span>
            </div>
            <div className="referral-stat highlight">
              <Crown size={20} />
              <span className="stat-number">{referralStats.converted}</span>
              <span className="stat-text">Mois gagnés</span>
            </div>
          </div>

          <div className="referral-invite">
            <div className="invite-input-group">
              <Mail size={18} />
              <input
                type="email"
                placeholder="Email de votre ami(e)"
                value={inviteEmail}
                onChange={(e) => { setInviteEmail(e.target.value); setInviteMessage(''); }}
              />
              <button onClick={handleInvite} disabled={inviteLoading}>
                {inviteLoading ? <Loader size={18} className="spin" /> : <Send size={18} />}
              </button>
            </div>
            {inviteMessage && (
              <p className={`invite-message ${inviteMessage.includes('🎉') ? 'success' : 'error'}`}>
                {inviteMessage}
              </p>
            )}
          </div>

          <div className="referral-link">
            <span>Ou partagez votre lien :</span>
            <div className="link-copy">
              <input 
                type="text" 
                readOnly 
                value={`moncoach.eu/?ref=${user?.id?.slice(0,8)}`}
              />
              <button onClick={copyReferralLink}>
                {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
                {copied ? 'Copié !' : 'Copier'}
              </button>
            </div>
          </div>
        </div>

        {/* Formulaire */}
        <div className="profile-form-card">
          <div className="form-card-header">
            <h2>📝 Mes informations</h2>
            {!editing ? (
              <button className="edit-btn" onClick={() => setEditing(true)}>
                <Edit3 size={16} /> Modifier
              </button>
            ) : (
              <button className="save-btn" onClick={handleSave} disabled={saving}>
                {saving ? <Loader size={16} className="spin" /> : <Check size={16} />}
                {saving ? 'Sauvegarde...' : 'Enregistrer'}
              </button>
            )}
          </div>

          <div className="profile-form">
            <div className="form-row">
              <div className="form-group">
                <label><User size={16} /> Prénom</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  disabled={!editing}
                  placeholder="Votre prénom"
                />
              </div>
              <div className="form-group">
                <label><Calendar size={16} /> Âge</label>
                <input
                  type="number"
                  value={formData.age}
                  onChange={e => setFormData({...formData, age: e.target.value})}
                  disabled={!editing}
                  placeholder="Ex: 30"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label><Scale size={16} /> Poids actuel (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.weight}
                  onChange={e => setFormData({...formData, weight: e.target.value})}
                  disabled={!editing}
                  placeholder="Ex: 70"
                />
              </div>
              <div className="form-group">
                <label><Target size={16} /> Poids cible (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.target_weight}
                  onChange={e => setFormData({...formData, target_weight: e.target.value})}
                  disabled={!editing}
                  placeholder="Ex: 65"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label><Ruler size={16} /> Taille (cm)</label>
                <input
                  type="number"
                  value={formData.height}
                  onChange={e => setFormData({...formData, height: e.target.value})}
                  disabled={!editing}
                  placeholder="Ex: 175"
                />
              </div>
              <div className="form-group">
                <label><Activity size={16} /> Niveau d'activité</label>
                <select
                  value={formData.activity_level}
                  onChange={e => setFormData({...formData, activity_level: e.target.value})}
                  disabled={!editing}
                >
                  <option value="">Sélectionner...</option>
                  {activityLevels.map(level => (
                    <option key={level.id} value={level.id}>{level.label} - {level.desc}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group full-width">
              <label>🎯 Mon objectif principal</label>
              <div className="goals-grid">
                {goals.map(goal => (
                  <button
                    key={goal.id}
                    type="button"
                    className={`goal-option ${formData.goal === goal.id ? 'active' : ''}`}
                    onClick={() => editing && setFormData({...formData, goal: goal.id})}
                    disabled={!editing}
                  >
                    <span className="goal-emoji">{goal.emoji}</span>
                    <span>{goal.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Préférences email */}
            <div className="form-group full-width">
              <label><Mail size={16} style={{display: 'inline', marginRight: '6px'}} />Préférences email</label>
              <div className="newsletter-toggle">
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={formData.email_newsletter}
                    onChange={(e) => editing && setFormData({...formData, email_newsletter: e.target.checked})}
                    disabled={!editing}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <div className="toggle-text">
                  <span className="toggle-label">Recevoir les recettes de la semaine</span>
                  <span className="toggle-desc">Chaque dimanche, 3 nouvelles recettes et un conseil personnalisé</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section changement email */}
        <div className="profile-form-card">
          <h2>✉️ Changer mon adresse email</h2>
          <p className="form-desc">Votre adresse actuelle : <strong>{user?.email}</strong></p>
          
          <div className="email-change-form">
            <div className="input-group-inline">
              <input
                type="email"
                placeholder="Nouvelle adresse email"
                value={newEmail}
                onChange={(e) => { setNewEmail(e.target.value); setEmailMessage(''); }}
              />
              <button onClick={handleEmailChange} disabled={emailLoading}>
                {emailLoading ? <Loader size={18} className="spin" /> : 'Modifier'}
              </button>
            </div>
            {emailMessage && (
              <p className={`form-message ${emailMessage.includes('envoyé') ? 'success' : 'error'}`}>
                {emailMessage}
              </p>
            )}
            <p className="form-note">
              Un email de confirmation sera envoyé aux deux adresses.
            </p>
          </div>
        </div>

        {/* Info calories */}
        {formData.weight && formData.height && formData.age && (
          <div className="calories-info-card">
            <h3>📊 Votre estimation calorique</h3>
            <p>Selon vos informations, votre besoin calorique quotidien estimé est d'environ :</p>
            <div className="calories-value">
              {calculateCalories(formData)} <span>kcal/jour</span>
            </div>
            <p className="calories-note">
              Cette estimation est basée sur la formule de Mifflin-St Jeor. Ajustez selon vos résultats réels.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Calcul des calories (Mifflin-St Jeor)
const calculateCalories = (data) => {
  const weight = parseFloat(data.weight) || 70;
  const height = parseFloat(data.height) || 170;
  const age = parseFloat(data.age) || 30;
  
  // BMR (homme par défaut)
  let bmr = 10 * weight + 6.25 * height - 5 * age + 5;
  
  // Multiplicateur activité
  const activityMultipliers = {
    'sedentary': 1.2,
    'light': 1.375,
    'moderate': 1.55,
    'active': 1.725,
    'very-active': 1.9
  };
  
  const multiplier = activityMultipliers[data.activity_level] || 1.4;
  let tdee = Math.round(bmr * multiplier);
  
  // Ajustement selon objectif
  if (data.goal === 'weight-loss') tdee -= 400;
  else if (data.goal === 'muscle-building') tdee += 300;
  
  return tdee;
};

export default ProfilePage;
